const PENNY_MM = 19.05;
const STORAGE_KEY = "luuve-nail-fit-px-per-mm";

// Width, then length, in millimeters. Length is cuticle to tip.
// Sizes 0–15, from the variant size charts.
const CHARTS = {
  "extra short almond": {
    file: "extra-short-almond",
    sizes: [
      [14.2, 18.5],
      [13, 17.9],
      [12.3, 17.6],
      [11.7, 17.2],
      [10.6, 16.6],
      [10.5, 16.1],
      [10, 15.6],
      [9.7, 15.4],
      [9.4, 15.2],
      [9.3, 14.8],
      [9.1, 14.6],
      [8.6, 14.1],
      [8.1, 13.7],
      [7.6, 13.3],
      [7.3, 12.9],
      [6.8, 12.5],
    ],
  },
  "extra short round": {
    file: "extra-short-round",
    sizes: [
      [16.4, 19.7],
      [14.7, 17.9],
      [13.4, 16.7],
      [12.9, 16.5],
      [12.2, 16.4],
      [11.2, 15.8],
      [10.6, 15.1],
      [9.93, 14.8],
      [9.5, 14.6],
      [8.9, 13.9],
      [8.5, 13.3],
      [8.2, 12.5],
      [7.6, 12.3],
      [7.1, 12.1],
      [6.6, 11.3],
      [9.3, 14.2],
    ],
  },
  "short square": {
    file: "short-square",
    sizes: [
      [14.6, 18.4],
      [13.4, 17.6],
      [12.4, 16.6],
      [12.11, 15.5],
      [10.8, 16.6],
      [10.7, 16.3],
      [10.3, 15.8],
      [9.9, 15.2],
      [9.4, 14.9],
      [8.8, 14.3],
      [8.5, 13.9],
      [8, 13.5],
      [7.6, 13.2],
      [7.2, 12.8],
      [6.9, 12.5],
      [9.4, 14.9],
    ],
  },
  "short almond": {
    file: "short-almond",
    sizes: [
      [14.6, 21.2],
      [14, 20.8],
      [12.9, 20.5],
      [11.8, 19.8],
      [10.9, 19.6],
      [10.7, 19.1],
      [10.2, 18.9],
      [9.9, 18.7],
      [9.6, 18.3],
      [9.1, 17.4],
      [8.8, 16.8],
      [8.3, 16.3],
      [7.9, 15.8],
      [7.3, 14.9],
      [7, 14.1],
      [9.4, 17.8],
    ],
  },
};

const CHART_NAMES = Object.keys(CHARTS).sort((a, b) => b.length - a.length);

function formatMm(value) {
  const rounded = Math.round(value * 100) / 100;
  return String(rounded);
}

function normalizeTitle(title) {
  return String(title || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function chartForTitle(title) {
  const key = normalizeTitle(title);
  const name = CHART_NAMES.find((chartName) => key === chartName || key.includes(chartName));
  if (!name) return null;
  return { name, ...CHARTS[name] };
}

function slideMarkup(sizeIndex, chart, pxPerMm, src) {
  const [widthMm, lengthMm] = chart.sizes[sizeIndex];
  const pixelW = widthMm * pxPerMm;
  const pixelH = lengthMm * pxPerMm;
  const label = `Size ${sizeIndex}, ${formatMm(widthMm)} by ${formatMm(lengthMm)} millimeters`;

  return `
    <div class="nail-fit__slide">
      <img class="nail-fit__art" src="${src}" alt="${label}" width="${Math.round(pixelW)}" height="${Math.round(pixelH)}" style="width:${pixelW}px;height:${pixelH}px">
    </div>
  `;
}

class NailFitGuide extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === "true") return;
    this.dataset.ready = "true";
    const modal = this.closest("modal-dialog");
    if (modal?.id) {
      document.querySelectorAll("modal-dialog").forEach((node) => {
        if (node !== modal && node.id === modal.id) node.remove();
      });
    }
    this.sizeIndex = 0;
    this.assets = this.readAssets();
    this.pxPerMm = this.readScale();
    this.pennyPx = this.pxPerMm ? this.pxPerMm * PENNY_MM : 96;
    this.variantTitle = "";
    this.sectionRoot = document.querySelector(`product-info[data-section="${this.dataset.section}"]`);
    this.modal = this.closest("modal-dialog");
    this.variantEl = this.querySelector("[data-variant]");
    this.titleEl = this.querySelector("[data-title]");
    this.calibrateEl = this.querySelector("[data-step='calibrate']");
    this.compareEl = this.querySelector("[data-step='compare']");
    this.pennyEl = this.querySelector("[data-penny]");
    this.rangeEl = this.querySelector("[data-penny-range]");
    this.viewerEl = this.querySelector("[data-viewer]");
    this.stageEl = this.querySelector("[data-stage]");
    this.sizeEl = this.querySelector("[data-size]");
    this.dimsEl = this.querySelector("[data-dims]");
    this.missingEl = this.querySelector("[data-missing]");
    this.bind();
    this.syncPenny();
    if (this.modal) {
      this.modal.addEventListener("open", () => this.onOpen());
    } else {
      this.onOpen();
    }
    document.addEventListener("variant:changed", (event) => {
      this.onVariantChanged(event);
    });
  }

  bind() {
    this.rangeEl?.addEventListener("input", () => {
      this.pennyPx = Number(this.rangeEl.value);
      this.syncPenny();
    });
    this.querySelectorAll("[data-penny-nudge]").forEach((button) => {
      const signedStep = Number(button.dataset.pennyNudge);
      const direction = Math.sign(signedStep) || 1;
      const step = Math.abs(signedStep) || 0.5;
      const nudge = (amount) => {
        this.pennyPx = Math.min(280, Math.max(40, this.pennyPx + amount));
        this.syncPenny();
      };
      button.addEventListener("pointerdown", (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        button.dataset.holding = "true";
        if (button.setPointerCapture) button.setPointerCapture(event.pointerId);
        this.stopPennyHold();
        nudge(direction * step);
        const started = performance.now();
        let last = started;
        const tick = (now) => {
          const held = now - started;
          const speed = held < 220 ? 0 : Math.min(90, 18 + (held - 220) / 18);
          if (speed > 0) nudge((direction * speed * (now - last)) / 1000);
          last = now;
          this.pennyHoldFrame = requestAnimationFrame(tick);
        };
        this.pennyHoldFrame = requestAnimationFrame(tick);
      });
      const release = () => {
        this.stopPennyHold();
        setTimeout(() => {
          button.dataset.holding = "false";
        }, 0);
      };
      button.addEventListener("pointerup", release);
      button.addEventListener("pointercancel", release);
      button.addEventListener("click", () => {
        if (button.dataset.holding === "true") return;
        nudge(direction * step);
      });
    });
    this.querySelector("[data-penny-confirm]")?.addEventListener("click", () => {
      this.pxPerMm = this.pennyPx / PENNY_MM;
      try {
        localStorage.setItem(STORAGE_KEY, String(this.pxPerMm));
      } catch (error) {
        /* Private browsing can block storage. The scale still applies for this visit. */
      }
      this.showStep("compare");
    });
    this.querySelector("[data-recalibrate]")?.addEventListener("click", () => {
      this.showStep("calibrate");
      this.querySelector("[data-penny-range]")?.focus();
    });
    this.querySelector("[data-close]")?.addEventListener("click", () => {
      this.modal?.hide();
    });
    this.stageEl?.addEventListener(
      "scroll",
      () => {
        if (this.dragging || this.animating) return;
        this.onStageScroll();
      },
      { passive: true }
    );
    this.stageEl?.addEventListener("pointerdown", (event) => this.onPointerDown(event));
    this.stageEl?.addEventListener("pointermove", (event) => this.onPointerMove(event));
    this.stageEl?.addEventListener("pointerup", (event) => this.onPointerUp(event));
    this.stageEl?.addEventListener("pointercancel", (event) => this.onPointerUp(event));
    this.stageEl?.addEventListener("keydown", (event) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.goTo(this.sizeIndex + 1);
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.goTo(this.sizeIndex - 1);
      }
    });
    this.querySelector("[data-size-stepper]")?.addEventListener("click", (event) => {
      const button = event.target.closest(".nail-fit__arrow");
      if (!button || button.disabled || !this.chart) return;
      this.goTo(this.sizeIndex + Number(button.dataset.step));
    });
  }

  stopPennyHold() {
    if (this.pennyHoldFrame) cancelAnimationFrame(this.pennyHoldFrame);
    this.pennyHoldFrame = 0;
  }

  readScale() {
    try {
      const value = Number(localStorage.getItem(STORAGE_KEY));
      if (!Number.isFinite(value) || value < 40 / PENNY_MM || value > 280 / PENNY_MM) return 0;
      return value;
    } catch (error) {
      return 0;
    }
  }

  renderVariant(title) {
    if (!this.variantEl) return;
    const source = this.querySelector("[data-variant-template]");
    const name = String(title || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");
    if (!source) {
      this.variantEl.textContent = title || "";
      return;
    }
    this.variantEl.innerHTML = source.innerHTML.replaceAll("[shape]", name);
  }

  readVariantTitle() {
    const node = this.sectionRoot?.querySelector("[data-selected-variant]");
    if (!node || !node.textContent.trim()) return "";
    try {
      return JSON.parse(node.textContent).title || "";
    } catch (error) {
      return "";
    }
  }

  onVariantChanged(event) {
    const title = event.detail?.variant?.title || this.readVariantTitle();
    if (!title) return;
    this.variantFromEvent = title;
    this.variantTitle = title;
    this.renderVariant(title);
    if (this.modal && !this.modal.hasAttribute("open")) return;
    if (!this.calibrateEl.hidden) return;
    this.renderNail();
  }

  onOpen() {
    const openerShape = this.modal?.openedBy?.dataset?.nailFitShape;
    const title = openerShape || this.variantFromEvent || this.readVariantTitle();
    this.variantFromEvent = "";
    this.variantTitle = title;
    this.renderVariant(title);
    this.showStep("calibrate");
    this.dataset.opened = "true";
  }

  showStep(step) {
    const calibrate = step === "calibrate";
    this.calibrateEl.hidden = !calibrate;
    this.compareEl.hidden = calibrate;
    if (this.titleEl) {
      const source = this.querySelector(calibrate ? "[data-title-calibrate]" : "[data-title-compare]");
      if (source) this.titleEl.innerHTML = source.innerHTML;
    }
    if (!calibrate) this.renderNail();
    this.titleEl?.focus();
  }

  syncPenny() {
    if (!this.pennyEl) return;
    this.pennyEl.style.width = `${this.pennyPx}px`;
    this.pennyEl.style.height = `${this.pennyPx}px`;
    if (this.rangeEl) this.rangeEl.value = String(this.pennyPx);
  }

  renderNail() {
    const chart = chartForTitle(this.variantTitle);
    this.chart = chart;
    if (!chart || !this.pxPerMm || !this.stageEl) {
      if (this.viewerEl) this.viewerEl.hidden = true;
      if (this.stageEl) this.stageEl.innerHTML = "";
      this.missingEl.hidden = false;
      return;
    }
    if (this.renderedFile !== chart.file) {
      this.sizeIndex = 0;
      this.renderedFile = chart.file;
    }
    this.sizeIndex = Math.max(0, Math.min(chart.sizes.length - 1, this.sizeIndex));
    this.missingEl.hidden = true;
    this.viewerEl.hidden = false;
    const tallest = Math.max(...chart.sizes.map((size) => size[1]));
    this.stageEl.style.minHeight = `${tallest * this.pxPerMm}px`;
    this.stageEl.innerHTML = chart.sizes
      .map((_, index) => slideMarkup(index, chart, this.pxPerMm, this.nailSrc(chart, index)))
      .join("");
    this.lockSlideWidth();
    this.stageEl.scrollLeft = this.sizeIndex * this.pageWidth();
    this.updateSizeReadout();
  }

  pageWidth() {
    return this.stageEl?.clientWidth || 0;
  }

  lockSlideWidth() {
    const page = this.pageWidth();
    if (!page) return;
    this.stageEl.querySelectorAll(".nail-fit__slide").forEach((slide) => {
      slide.style.flex = `0 0 ${page}px`;
      slide.style.width = `${page}px`;
      slide.style.minWidth = `${page}px`;
      slide.style.maxWidth = `${page}px`;
    });
  }

  onPointerDown(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target.closest(".nail-fit__arrow")) return;
    if (!this.chart || !this.stageEl) return;
    this.stageEl.classList.add("is-dragging");
    this.dragging = true;
    this.stopSettle();
    this.drag = { x: event.clientX, scroll: this.stageEl.scrollLeft, pointerId: event.pointerId, index: this.sizeIndex };
    this.stageEl.setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    event.preventDefault();
    this.stageEl.scrollLeft = this.drag.scroll - (event.clientX - this.drag.x);
  }

  onPointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const dx = event.clientX - this.drag.x;
    const startIndex = this.drag.index;
    this.drag = null;
    this.dragging = false;
    if (dx <= -40) this.goTo(startIndex + 1);
    else if (dx >= 40) this.goTo(startIndex - 1);
    else this.goTo(startIndex);
  }

  onStageScroll() {
    if (!this.chart || !this.stageEl || this.stageEl.clientWidth === 0) return;
    const index = Math.round(this.stageEl.scrollLeft / this.stageEl.clientWidth);
    const next = Math.max(0, Math.min(this.chart.sizes.length - 1, index));
    if (next === this.sizeIndex) return;
    this.sizeIndex = next;
    this.updateSizeReadout();
  }

  goTo(index) {
    if (!this.chart || !this.stageEl || !Number.isFinite(index)) return;
    const next = Math.max(0, Math.min(this.chart.sizes.length - 1, index));
    this.sizeIndex = next;
    this.updateSizeReadout();
    const page = this.pageWidth();
    if (!page) return;
    this.settleTo(next * page);
  }

  stopSettle() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    this.animFrame = 0;
    this.animating = false;
    if (!this.dragging) this.stageEl?.classList.remove("is-settling");
  }

  settleTo(target) {
    const stage = this.stageEl;
    const from = stage.scrollLeft;
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    this.animFrame = 0;
    stage.classList.add("is-settling");
    stage.classList.remove("is-dragging");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || Math.abs(target - from) < 1) {
      stage.scrollLeft = target;
      this.animating = false;
      stage.classList.remove("is-settling");
      return;
    }
    this.animating = true;
    const start = performance.now();
    const duration = 340;
    const step = (now) => {
      if (this.dragging) return;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      stage.scrollLeft = from + (target - from) * eased;
      if (t < 1) this.animFrame = requestAnimationFrame(step);
      else {
        stage.scrollLeft = target;
        this.animating = false;
        this.animFrame = 0;
        stage.classList.remove("is-settling");
      }
    };
    this.animFrame = requestAnimationFrame(step);
  }

  updateSizeReadout() {
    if (!this.chart) return;
    const [widthMm, lengthMm] = this.chart.sizes[this.sizeIndex];
    if (this.sizeEl) this.sizeEl.textContent = `Size ${this.sizeIndex}`;
    if (this.dimsEl) this.dimsEl.textContent = `${formatMm(widthMm)} × ${formatMm(lengthMm)} mm`;
    this.querySelectorAll(".nail-fit__arrow").forEach((button) => {
      const direction = Number(button.dataset.step);
      const atStart = direction < 0 && this.sizeIndex === 0;
      const atEnd = direction > 0 && this.sizeIndex === this.chart.sizes.length - 1;
      button.disabled = atStart || atEnd;
    });
  }

  readAssets() {
    const node = this.querySelector("[data-nail-assets]");
    if (!node || !node.textContent.trim()) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  nailSrc(chart, sizeIndex) {
    const listed = this.assets?.[chart.file]?.[sizeIndex];
    if (listed) return listed;
    const fileIndex = String(sizeIndex + 1).padStart(3, "0");
    return `/assets/nail-fit-${chart.file}-${fileIndex}.png`;
  }
}

if (!customElements.get("nail-fit-guide")) {
  customElements.define("nail-fit-guide", NailFitGuide);
}

if (document.documentElement.dataset.fitGuideScroll !== "true") {
  document.documentElement.dataset.fitGuideScroll = "true";
  document.addEventListener("click", (event) => {
    const link = event.target.closest("[data-fit-guide-scroll]");
    if (!link) return;
    const target = document.querySelector(link.getAttribute("href") || "");
    if (target) {
      event.preventDefault();
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      target.focus({ preventScroll: true });
      return;
    }
    const modal = document.querySelector(link.dataset.modal || "");
    if (!modal?.show) return;
    event.preventDefault();
    modal.show(link);
  });
}
